import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField as MuiTextField,
	Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WarningIcon from "@material-ui/icons/Warning";
import React, { useState } from "react";
import { Confirm, useTranslate } from "react-admin";

import { ExtractionSummaryHeader } from "./ExtractionSummaryHeader";
import { PreviewRowsTable } from "./PreviewRowsTable";
import { usePdfExtractionPreview } from "./usePdfExtractionPreview";

const useStyles = makeStyles((theme) => ({
	hint: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
		color: theme.palette.warning.dark,
	},
	clarification: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		marginTop: theme.spacing(2),
	},
	clarificationInput: { flex: 1 },
}));

export const PdfExtractionPreviewDialog = ({ open, onClose, order, preview, onSaved }) => {
	const classes = useStyles();
	const translate = useTranslate();

	const {
		extraction,
		rows,
		hasEdits,
		isReparsing,
		isSaving,
		isBusy,
		changeCell,
		changeWeightUnit,
		reparse,
		save,
	} = usePdfExtractionPreview({ order, preview, onSaved });

	const [clarification, setClarification] = useState("");
	const [confirmingReparse, setConfirmingReparse] = useState(false);

	const handleReparseClick = () => {
		if (hasEdits) {
			setConfirmingReparse(true);
			return;
		}
		reparse(clarification);
	};

	const handleConfirmReparse = () => {
		setConfirmingReparse(false);
		reparse(clarification);
	};

	return (
		<Dialog
			open={open}
			onClose={isBusy ? undefined : onClose}
			disableBackdropClick={isBusy}
			disableEscapeKeyDown={isBusy}
			maxWidth="xl"
			fullWidth
			scroll="body"
		>
			<DialogTitle>{translate("osfc.dialogs.generate-pallets-from-pdf.title")}</DialogTitle>
			<DialogContent>
				<div className={classes.hint}>
					<WarningIcon fontSize="small" />
					<Typography variant="body2">
						{translate("osfc.dialogs.generate-pallets-from-pdf.review_hint")}
					</Typography>
				</div>

				<ExtractionSummaryHeader
					extraction={extraction}
					rows={rows}
					disabled={isBusy}
					onWeightUnitChange={changeWeightUnit}
				/>

				<PreviewRowsTable rows={rows} onCellChange={changeCell} />

				<div className={classes.clarification}>
					<MuiTextField
						className={classes.clarificationInput}
						label={translate("osfc.dialogs.generate-pallets-from-pdf.clarification")}
						placeholder={translate(
							"osfc.dialogs.generate-pallets-from-pdf.clarification_placeholder",
						)}
						helperText={translate("osfc.dialogs.generate-pallets-from-pdf.clarification_hint")}
						value={clarification}
						onChange={(e) => setClarification(e.target.value)}
						disabled={isBusy}
						multiline
						rowsMax={4}
						inputProps={{ maxLength: 2000 }}
					/>
					<Button
						variant="outlined"
						color="primary"
						onClick={handleReparseClick}
						disabled={isBusy || !clarification.trim()}
					>
						{isReparsing && (
							<CircularProgress size={18} thickness={2} style={{ marginRight: 8 }} />
						)}
						{translate("osfc.dialogs.generate-pallets-from-pdf.reparse")}
					</Button>
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={isBusy}>
					{translate("osfc.buttons.cancel")}
				</Button>
				<Button
					variant="contained"
					color="primary"
					onClick={save}
					disabled={isBusy || rows.length === 0}
				>
					{isSaving && <CircularProgress size={18} thickness={2} style={{ marginRight: 8 }} />}
					{translate("osfc.dialogs.generate-pallets-from-pdf.confirm")}
				</Button>
			</DialogActions>

			<Confirm
				isOpen={confirmingReparse}
				title="osfc.dialogs.generate-pallets-from-pdf.reparse_confirm_title"
				content="osfc.dialogs.generate-pallets-from-pdf.reparse_confirm"
				confirm="osfc.buttons.yes"
				cancel="osfc.buttons.no"
				onConfirm={handleConfirmReparse}
				onClose={() => setConfirmingReparse(false)}
			/>
		</Dialog>
	);
};
